import styled, { css } from '@xstyled/styled-components';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Item, registry, RenderProps } from '../Item';
import { BULLET } from '../itemIds';
import CheckboxIcon from './checkbox.svg';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { shallow } from 'zustand/shallow';
import { useEditorStore } from '~/shell/Store';
import { Text } from '../text/Text';
import { TEXT } from '../itemIds';
import { TrashIconForBtn } from '~/training/sequence/templates/items';
import LightButton from '~/editor/buttons/LightButton';
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';

const selector = (s: MediaPlayerStore) =>
  select(s, 'currentTime', 'status', 'mediaSrc');

interface Props {
  bullet?: string | number | 'checkmark';
  start?: number;
  value: string;
}

interface LiProps {
  bullet?: string | number | 'checkmark' | undefined;
  isContentEditableMode?: boolean;
}

const DragAndDropIconForBtn = styled(DragAndDropIcon)`
  cursor: move !important;
  z-index: 100;
  margin-top: 8px;
  width: 20px;
  height: 20px;
`;

const Chevron = styled(CheckboxIcon)`
  float: left;
  margin-right: 10px;
  rect {
    fill: var(--color-accent-dk);
  }
`;

const WrapperForLineItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-bottom: 5px;
`;

const WrapperForTimer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 4px 4px 4px 16px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  border-radius: 12px;
  border: 1px solid #e0e0e0;

  margin-top: 5px;
  padding-top: 12px;

  color: #6f6f6f;
  font-family: Raleway;
  font-size: 14px;

  input {
    margin-top: 4px;
    color: #6f6f6f;
    background: transparent;
    font-variant-numeric: lining-nums proportional-nums;
    font-family: ralewayBold;
    font-size: 14px;
    width: 36px;
    text-align: center;
  }
`;

const Li = styled.liBox<LiProps>`
  border-top: 2px solid transparent;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;

  b {
    font-family: recoletaBold !important;
  }
  a {
    text-decoration: underline !important;
  }

  ${p =>
    p.isContentEditableMode &&
    css`
      margin-bottom: 5px !important;
    `}

  &.drag-over-top {
    border-top: 2px solid #9845fb !important;
    border-bottom: 2px solid transparent !important;
    padding-top: 10px;
    background-color: rgba(152, 69, 251, 0.05);
  }

  &.drag-over-bottom {
    border-top: 2px solid transparent !important;
    border-bottom: 2px solid #9845fb !important;
    padding-bottom: 10px;
    background-color: rgba(152, 69, 251, 0.05);
  }
  ${p => {
    if (typeof p.bullet == 'number') {
      return css`
        list-style-type: none;
        padding-left: 10px;
        ::before {
          content: '${p.bullet}';
          background-color: #9738ff;
          color: white;
          border-radius: 50%;
          text-align: center;
          padding-top: 2px;
          margin-right: 10px;
          width: ${p.isContentEditableMode ? '38px' : '32px'};
          height: 32px;
          display: block;
          float: left;
          position: relative;
          margin-top: ${p.isContentEditableMode ? '5px' : '-5px'};
          font-family: ralewayBold;
        }
        p {
          padding-left: 41px;
        }
      `;
    }

    if (typeof p.bullet == 'string' && p.bullet != 'checkmark') {
      return css`
        padding-left: 7px;
        p {
          padding-left: 41px;
        }
      `;
    }

    if (typeof p.bullet == 'string' && p.bullet == 'checkmark') {
      return css`
        list-style-type: none;
        p {
          padding-left: 41px;
        }
      `;
    }

    if (typeof p.bullet == 'undefined') {
      return css`
        list-style-type: disc;
        margin-left: 20px;
      `;
    }

    return '';
  }};

  .draggable-content {
    width: 100%;
    padding-top: 8px;
  }

  .draggable-icon {
    cursor: move !important;
    display: flex;
    alignitems: center;
    height: 28px;
    margin-top: 1px;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
    user-select: none;
    margin-left: -5px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  &.draggable {
    display: flex;
    flex-direction: row;
  }

  .add-timer-button {
    padding: '11px';
    height: '38px';
    display: 'block';
    z-index: 2;
    margin-top: -7px;
  }

  .remove-timer-button {
  }
`;

export class Bullet extends Item<typeof BULLET, Props> {
  protected render({ id, bullet, value, start = 0 }: RenderProps<Props>) {
    const {
      contentEditable,
      sequence,
      removeSubItem,
      updateSubItem,
      setSequence
    } = useEditorStore();
    const { currentTime, status, mediaSrc } = useMediaPlayerStore(
      selector,
      shallow
    );
    const [showItem, setShowItem] = useState(!start);
    const [renderElement, setRenderElement] = useState(true);
    const [startValue, setStartValue] = useState<number | string>(start);
    const [isDragOver, setIsDragOver] = useState(false);
    const [classNameForDragOver, setClassNameForDragOver] =
      useState<string>('');
    const [canDrag, setCanDrag] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      //for content editable mode we need to show the item
      if (contentEditable) {
        setShowItem(true);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      if (!start || start === 0) {
        setShowItem(true);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      const isNoMedia = status === 'nomedia';
      const definitelyHasMedia = mediaSrc && mediaSrc !== '';
      const mediaNotYetDetermined = status !== 'nomedia' && !definitelyHasMedia;

      if (mediaNotYetDetermined) {
        return;
      }

      if (definitelyHasMedia) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        if (start <= currentTime || status === 'done') {
          setShowItem(true);
        } else {
          setShowItem(false);
        }
      } else if (isNoMedia) {
        if (!timerRef.current) {
          setShowItem(false);

          timerRef.current = setTimeout(() => {
            setShowItem(true);
            timerRef.current = null;
          }, start * 1000);
        }
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }, [currentTime, status, start, contentEditable, mediaSrc]);

    // Reset drag states when component unmounts or drag ends globally
    useEffect(() => {
      if (!contentEditable) return;

      const handleGlobalDragEnd = () => {
        setIsDragOver(false);
        setClassNameForDragOver('');
      };

      document.addEventListener('dragend', handleGlobalDragEnd);

      return () => {
        document.removeEventListener('dragend', handleGlobalDragEnd);
        setIsDragOver(false);
        setClassNameForDragOver('');
      };
    }, [contentEditable]);

    const updateAppear = (value: number | string) => {
      setStartValue(value);
      updateSubItem(id.split('_')[0], 'appear', value);
    };

    const handleDragStart = (e: React.DragEvent<HTMLElement>) => {
      e.dataTransfer.setData('text/plain', id.split('_')[0]);
      if (window) {
        window.sessionStorage.setItem('dragItemId', id.split('_')[0]);
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setIsDragOver(true);

      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const height = rect.height;

      if (y < height / 2) {
        setClassNameForDragOver('drag-over-top');
      } else {
        setClassNameForDragOver('drag-over-bottom');
      }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLElement>) => {
      if (!contentEditable || !sequence) {
        return;
      }

      e.preventDefault();
      setIsDragOver(false);

      const dragItemId = e.dataTransfer.getData('text/plain');

      if (!sequence) return;

      const textItem = sequence.items.find(
        (item: { type: string }) => item.type === 'LIST'
      );
      if (!textItem || !textItem.items) return;

      const currentId = (e.currentTarget.closest('[data-id]') as HTMLElement)
        ?.dataset.id;
      if (!currentId) return;

      const dragIndex = textItem.items.findIndex(
        (item: { id: string }) => item.id == dragItemId
      );

      const dropIndex = textItem.items.findIndex(
        (item: { id: string }) => item.id == currentId
      );

      if (dragIndex === -1 || dropIndex === -1 || dragItemId === currentId)
        return;

      // Create new items array with updated positions
      const newItems = [...textItem.items];
      const [draggedItem] = newItems.splice(dragIndex, 1);
      newItems.splice(dropIndex, 0, draggedItem);

      // Update positions in the new array
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        position: index + 1
      }));

      // Optimistically update the sequence immediately
      const updatedSequence = {
        ...sequence,
        items: sequence.items.map((item: { type: string; items?: any[] }) =>
          item.type === 'LIST' ? { ...item, items: updatedItems } : item
        )
      };

      setSequence(updatedSequence);

      // Update all items positions via API (don't wait for response)
      updatedItems.forEach((item, index) => {
        if (item.id == dragItemId) {
          updateSubItem(
            item.id,
            'position',
            dragIndex < dropIndex ? index + 2 : index + 1
          ).catch(error => {
            console.error('Failed to update position:', error);
          });
        }
      });
    };

    const Icon = () =>
      typeof bullet == 'string' && bullet == 'checkmark' ? <Chevron /> : <></>;

    const text =
      typeof bullet == 'string' && bullet !== 'checkmark'
        ? `<span aria-hidden="true">${bullet}</span> ${value}`
        : value;

    const TextComponent = useMemo((): React.ComponentType | null => {
      if (!contentEditable) return null;

      const textComponent = new Text({
        id: `${id}_title`,
        type: TEXT,
        values: {
          value: value,
          variant: 'text',
          textAlignment: false
        },
        items: [],
        contentEditable: true
      });

      return textComponent.component();
    }, [contentEditable, bullet, value, text, id]);

    const handleDelete = () => {
      const subitemId = id.split('_')[0];
      removeSubItem(subitemId);
      setRenderElement(false);
    };

    const getCountOfItems = () =>
      sequence?.items?.filter((item: any) => item.type == 'LIST')[0]?.items
        ?.length;

    if (!renderElement) {
      return null;
    }

    return contentEditable ? (
      <Li
        bullet={bullet}
        visibility={showItem ? 'visible' : 'hidden'}
        className={`draggable ${isDragOver ? classNameForDragOver : ''}`}
        draggable={contentEditable && canDrag}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-id={id.split('_')[0]}
        isContentEditableMode={contentEditable}
      >
        <div
          className="draggable-icon"
          onMouseEnter={() => setCanDrag(true)}
          onMouseLeave={() => setCanDrag(false)}
        >
          <DragAndDropIconForBtn />
        </div>
        <div className="draggable-content">
          <Icon />
          {TextComponent && (
            <WrapperForLineItem>
              {React.createElement(TextComponent)}
              {getCountOfItems() > 1 && (
                <LightButton
                  title="Remove"
                  onClick={handleDelete}
                  style={{
                    padding: '11px',
                    width: '38px',
                    height: '38px',
                    display: 'block',
                    marginLeft: 'auto',
                    marginTop: '-5px',
                    marginRight: '5px',
                    zIndex: 2
                  }}
                >
                  <TrashIconForBtn
                    style={{ marginLeft: '-5px', marginTop: '-5px' }}
                  />
                </LightButton>
              )}
            </WrapperForLineItem>
          )}
          {startValue !== 0 &&
            startValue !== null &&
            startValue !== undefined && (
              <WrapperForTimer>
                Show after:
                <input
                  type="text"
                  inputMode="decimal"
                  name="appear"
                  id="appear"
                  value={startValue || ''}
                  onChange={e => {
                    const value = e.target.value;
                    if (value === '' || /^[\d.]*$/.test(value)) {
                      setStartValue(value);
                    } else {
                      setStartValue(1);
                    }
                  }}
                  onBlur={() => {
                    if (startValue === '' || startValue === '.') {
                      // Don't update if empty - let user continue editing
                      return;
                    }
                    const numericValue = parseFloat(
                      startValue as unknown as string
                    );
                    if (!isNaN(numericValue)) {
                      updateAppear(numericValue);
                    }
                  }}
                  placeholder="0"
                />
                seconds
                <LightButton
                  title="Remove"
                  onClick={() => updateAppear(0)}
                  style={{
                    padding: '11px',
                    width: '38px',
                    height: '38px',
                    display: 'block',
                    marginLeft: 'auto',
                    zIndex: 2,
                    marginTop: '-7px'
                  }}
                >
                  <TrashIconForBtn
                    style={{ marginLeft: '-5px', marginTop: '-5px' }}
                  />
                </LightButton>
              </WrapperForTimer>
            )}
          {startValue === 0 && (
            <LightButton
              title="Add Timer"
              onClick={() => updateAppear(1)}
              className="add-timer-button"
            >
              Add Timer
            </LightButton>
          )}
        </div>
      </Li>
    ) : (
      <Li
        bullet={bullet}
        visibility={showItem ? 'visible' : 'hidden'}
        data-id={id}
      >
        <Icon />
        <p dangerouslySetInnerHTML={{ __html: text }} />
      </Li>
    );
  }
}

registry[BULLET] = Bullet;
